import configData from "./../config.js";

const SCRIPT_ID = "google-recaptcha-v3";

let loadPromise = null;

export const RECAPTCHA_SITE_KEY = configData.RECAPTCHA_SITE_KEY;

// The script is injected on demand rather than from index.html so that the
// badge is never baked into the react-snap prerendered markup, and so that
// pages without a form do not pay for the download.
function loadRecaptcha() {
  if (loadPromise) {
    return loadPromise;
  }
  if (typeof window === "undefined" || !RECAPTCHA_SITE_KEY) {
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }
  if (window.grecaptcha && window.grecaptcha.execute) {
    loadPromise = Promise.resolve(window.grecaptcha);
    return loadPromise;
  }

  loadPromise = new Promise((resolve) => {
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      document.head.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.grecaptcha || null));
    script.addEventListener("error", () => {
      console.warn("reCAPTCHA script failed to load");
      resolve(null);
    });
  });

  return loadPromise;
}

// Warms the script up on the first real user interaction so the initial form
// submit does not have to wait for the download.
export function preloadRecaptcha() {
  if (typeof window === "undefined" || !RECAPTCHA_SITE_KEY) {
    return;
  }
  const events = ["pointerdown", "keydown", "touchstart"];
  const warm = () => {
    events.forEach((name) => window.removeEventListener(name, warm));
    loadRecaptcha();
  };
  events.forEach((name) =>
    window.addEventListener(name, warm, { once: true, passive: true })
  );
}

// Resolves to a token, or null when reCAPTCHA is unavailable (no key, blocked
// script, offline). Callers stay fail-open: the API is the place that decides
// what to do with a missing token, so an ad blocker never eats a real lead.
export async function executeRecaptcha(action) {
  try {
    const grecaptcha = await loadRecaptcha();
    if (!grecaptcha || !grecaptcha.execute) {
      return null;
    }
    return await new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action })
          .then(resolve)
          .catch((error) => {
            console.warn("reCAPTCHA execute failed", error);
            resolve(null);
          });
      });
    });
  } catch (error) {
    console.warn("reCAPTCHA execute failed", error);
    return null;
  }
}

// Fields to merge into a form's request body. Empty when no token could be
// obtained, so existing payloads are unchanged in that case.
export async function getRecaptchaFields(action) {
  const token = await executeRecaptcha(action);
  if (!token) {
    return {};
  }
  return {
    recaptcha_token: token,
    recaptcha_action: action,
    "g-recaptcha-response": token,
  };
}
